import type { Observable } from 'rxjs';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    distinctUntilChanged,
    EMPTY,
    filter,
    map,
    of,
    startWith,
    switchMap,
} from 'rxjs';
import { useObservable, useObservableEagerState } from 'observable-hooks';
import { first, isArray, isEmpty, isString } from 'lodash-es';
import type { FieldValues } from 'react-hook-form';

import { ExternalApiTarget } from '@/graphql/types';
import type { SwarmFeaturesFieldsFragment } from '@/graphql/types';
import { schema } from '../../../schema';
import { featureCheckProject$, featureCheckSwarm$ } from './featureCheck';
import warning from '../../../utils/logger';

import { useSharedFormContext } from '../../reactForm/utils';
import { tryToBoolean } from '../../../utils/cast';
import { shareReplayRefCount } from '../../../utils/rxjs';
import { FormValuesHandler } from './handlers';
import { SwarmType } from '../../../enums';
import type { SwarmFormController as FormController } from './form-controller';

export const ANY = '__any__';

type FieldName = string;
type OptionValue = string | boolean | number;
type ConfigFieldsMap = Map<FieldName | boolean, ConfigFieldsMap>;
export type AvailableFields = Record<FieldName, typeof ANY | OptionValue[]>;
type DisabledFields = string[];

function hashFieldOption(field?: FieldName | boolean | null, option?: OptionValue | null): string {
    return [field, option].join();
}

function getAvailableFieldsAndOptions(
    fields: ConfigFieldsMap,
    disabledFields: DisabledFields,
    values: FieldValues,
    setValue: (field: FieldName, value: OptionValue) => void,
    availableFields: AvailableFields = {}
): AvailableFields {
    // All fields considered disabled until proven otherwise.
    fields.forEach((options, field) => {
        const canBeAdded = !disabledFields.includes(hashFieldOption(field, ANY));
        const optionKeys = Array.from(options.keys());

        // Fields without specific options need to be treated differently.
        if (optionKeys.includes(ANY)) {
            // Make sure this field is not disabled before we enable it.
            if (canBeAdded) {
                availableFields[field as string] = ANY;
            }
            // No way to traverse down any fields.
            return;
        }

        const availableOptions = optionKeys.filter(
            (option) => !disabledFields.includes(hashFieldOption(field, option))
        );

        // Prevent it from getting disabled later.
        if (canBeAdded) {
            availableFields[field as string] = availableOptions;
        }

        let currentOption = values[field as string] as string;

        if (!availableOptions.includes(currentOption)) {
            // Our current option is not available anymore, change it.
            // eslint-disable-next-line prefer-destructuring
            currentOption = availableOptions[0] as string;
            setValue(field as string, currentOption);
        }

        // Iterate down the current option.
        getAvailableFieldsAndOptions(
            options.get(currentOption)!,
            disabledFields,
            values,
            setValue,
            availableFields
        );
    });

    return availableFields;
}

// Here we convert the config helper choices table from recursive objects into recursive Map's.
// This is necessary because objects in JS cannot contain booleans as keys, while those of Map's
// can. If we were to use objects, the boolean choices for boolean fields would be cast in strings.
// If a form later sends an update of a boolean field to the configuration helper, the value send
// will be a true boolean. If we then had to look up the choice in the configuration helper, we
// would have to convert the real boolean into a string boolean to make the comparison. Then, once
// we've found the option and the configuration helper has decided that a different choice should be
// selected for that field, we'll need to convert the boolean string back to a true boolean for the
// form to use again. All in all, it is therefore easier to do all these conversions in advance and
// then save them in a Map.
function convertConfigFieldsIntoMap(configFields: Record<string, unknown>): ConfigFieldsMap {
    return new Map(
        Object.entries(configFields).map(([field, options]) => [
            tryToBoolean(field),
            convertConfigFieldsIntoMap(options as Record<string, unknown>),
        ])
    );
}

export class ConfigHelper extends FormValuesHandler {
    declare formController: FormController;

    declare swarmType: SwarmType;

    declare availableFields$: BehaviorSubject<AvailableFields>;

    declare currentlySelectedOptions$: BehaviorSubject<Record<string, OptionValue>>;

    declare allFields: string[];

    declare configFields: ConfigFieldsMap;

    declare enabled: boolean;

    declare deviceName$: Observable<string | null>;

    declare swarmFeatures$: Observable<SwarmFeaturesFieldsFragment[]>;

    declare disabledFeatures$: Observable<DisabledFields>;

    declare deviceNameCorrection$: Observable<string>;

    constructor(formController: FormController) {
        super();

        this.init(formController);
    }

    init(formController: FormController): void {
        this.formController = formController;
        this.swarmType = formController.swarmType;
        this.availableFields$ = new BehaviorSubject<AvailableFields>({});
        this.currentlySelectedOptions$ = new BehaviorSubject<Record<string, OptionValue>>({});
        this.allFields = [...schema.configHelper.allFields];
        this.configFields = this.transformConfigFields(schema.configHelper.configFields);
        this.enabled = true;
        this.deviceName$ = this.createDeviceName$();
        // Adding `shareReplayRefCount` as a cache here because the emitted values
        // are used by the feature disabling, device name corrector, and the disabled
        // features banner.
        this.swarmFeatures$ = this.createSwarmFeatures$().pipe(shareReplayRefCount());
        this.disabledFeatures$ = this.createDisabledFeatures$();
        this.deviceNameCorrection$ = this.createDeviceNameCorrection$();

        combineLatest([this.values$, this.disabledFeatures$]).subscribe(
            ([values, disabledFields]) => {
                const currentlySelectedOptions: Record<string, OptionValue> = {};

                function setFieldValue(field: FieldName, value: OptionValue): void {
                    currentlySelectedOptions[field] = value;
                }

                const availableFields = getAvailableFieldsAndOptions(
                    this.configFields,
                    disabledFields,
                    values,
                    setFieldValue
                );

                this.availableFields$.next(availableFields);

                if (!isEmpty(currentlySelectedOptions)) {
                    // If corrections are found, apply them. This will trigger a new
                    // `values$` emission, restarting the entire chain.
                    Object.entries(currentlySelectedOptions).forEach(([field, value]) => {
                        this.formController.rhfProps.formControl.setValue(field, value);
                    });
                    return;
                }

                // If no corrections were made, the state is stable.
                // Emit the values to signal that the next handler can proceed.
                this.emitStable(values);
            }
        );
    }

    isEnabledForField(fieldName: FieldName | FieldName[]): boolean {
        return (
            this.enabled &&
            // And check if this fieldName is part of the config helper fields.
            // `fieldName` could also be an array of field names.
            (isArray(fieldName) ? fieldName : [fieldName]).some((name) =>
                this.allFields.includes(name)
            )
        );
    }

    // Performs the Map conversion, but also allows extension by inheriting classes to
    // make other changes to the configuration fields.
    transformConfigFields(configFields: typeof schema.configHelper.configFields): ConfigFieldsMap {
        const swarmTypeSpecificConfigFields = configFields.swarmType[this.swarmType];

        if (!swarmTypeSpecificConfigFields) {
            throw new Error(`Config helper has no support for SWARM type '${this.swarmType}'.`);
        }

        return convertConfigFieldsIntoMap(
            // Grab the right set of fields for this SWARM type.
            configFields.swarmType[this.swarmType]
        );
    }

    createDeviceName$(): Observable<string | null> {
        return this.values$.pipe(
            map((values) => {
                const { deviceName } = values as { deviceName?: string };
                return deviceName && deviceName.length === 6 ? deviceName : null;
            }),
            distinctUntilChanged(),
            shareReplayRefCount()
        );
    }

    createSwarmFeatures$(): Observable<SwarmFeaturesFieldsFragment[]> {
        return this.deviceName$.pipe(
            switchMap((deviceName) => {
                if (!deviceName) {
                    return of([]);
                }

                return featureCheckSwarm$(deviceName).pipe(
                    catchError((error: Error) => {
                        warning(error);
                        return of([]);
                    }),
                    startWith([])
                );
            })
        );
    }

    createDisabledFeatures$(): Observable<DisabledFields> {
        return this.swarmFeatures$.pipe(
            // Returns an array like:
            // [
            //     'guideLine,SS_025211',
            //     'guideLine,SS_4604861',
            // ]
            map((swarmFeatures) => {
                if (isEmpty(swarmFeatures)) {
                    return Object.entries(
                        schema.configHelper.neverAllowedFeatures[this.swarmType]
                    ).map(([field, option]) => hashFieldOption(field, option));
                }

                // Loop over all the devices.
                return swarmFeatures.flatMap(({ unavailableFeatures }) =>
                    // Map all the unavailableFeatures of all devices into one big list.
                    unavailableFeatures!.map((feature) =>
                        hashFieldOption(feature!.field, feature!.option)
                    )
                );
            })
        );
    }

    // This function creates an observable that emits the corrected device name.
    // It achieves this by combining the response from the SWARM features request
    // and the device name entered into the form. If the backend sanitizes the
    // device name, the observable will emit the corrected name.
    createDeviceNameCorrection$(): Observable<string> {
        return combineLatest([this.deviceName$, this.swarmFeatures$]).pipe(
            // Extract the device name from the SWARM features data.
            map(
                ([deviceName, swarmFeatures]) =>
                    [deviceName, first(swarmFeatures)?.deviceName] as const
            ),
            // Only pass emissions where both names are strings and they do not match.
            filter(
                ([deviceName, correctedName]) =>
                    isString(deviceName) && isString(correctedName) && deviceName !== correctedName
            ),
            // Finally, only emit the corrected device name.
            map(([_deviceName, correctedName]) => correctedName!)
        );
    }
}

export class ProjectConfigHelper extends ConfigHelper {
    projectId?: string;

    init(formController: FormController): void {
        this.projectId = formController.id;

        super.init(formController);
    }

    // TODO: https://github.com/omnidots/website/issues/9677
    transformConfigFields(configFields: typeof schema.configHelper.configFields): ConfigFieldsMap {
        const projectConfigFields = super.transformConfigFields(configFields);

        if (this.swarmType !== SwarmType.Sound) {
            return projectConfigFields;
        }

        // Sound projects cannot configure an external API target. Keep applying the
        // regular sound configuration rules without registering that measuring-point-only
        // field in the project form.
        const noExternalApiConfigFields = projectConfigFields
            .get('externalApiTarget')
            ?.get(ExternalApiTarget.NoExternalApi);

        if (!noExternalApiConfigFields) {
            throw new Error(
                'Sound project config helper requires the noExternalApi branch for externalApiTarget.'
            );
        }

        return noExternalApiConfigFields;
    }

    createSwarmFeatures$(): Observable<SwarmFeaturesFieldsFragment[]> {
        if (!this.projectId) {
            return of([]);
        }

        return featureCheckProject$(this.projectId).pipe(
            catchError((error: Error) => {
                warning(error);
                return of([]);
            })
        );
    }
}

export class DisabledConfigHelper extends ConfigHelper {
    constructor() {
        super({ swarmType: SwarmType.Vibration } as FormController);
        this.enabled = false;
    }

    updateValues(values: FieldValues) {
        // No mutations needed when disabled, directly mark these values as stable.
        this.emitStable(values);
    }

    createSwarmFeatures$(): Observable<never> {
        return EMPTY;
    }
}

export function useHiddenByConfigHelper(name: FieldName): boolean {
    const { formController } = useSharedFormContext();

    const hidden$ = useObservable(
        (input$) =>
            input$.pipe(
                switchMap(([n, c]) => {
                    const enabled = c.enabled && c.allFields.includes(n);

                    // If the config helper is turned off or the field is not part of the
                    // config helper, the field is visible by default.
                    if (!enabled) {
                        return of(false);
                    }

                    // If the field is not available inside `availableFields`, then it should
                    // be hidden.
                    return c.availableFields$.pipe(
                        map((availableFields) => !(n in availableFields)),
                        distinctUntilChanged()
                    );
                })
            ),
        [name, formController.configHelper]
    );

    return useObservableEagerState(hidden$);
}
